(function (window) {
  window.componentEvents.on('consumer-rx-form-instantiate', init);

  /**
   * Instantiate conditional fields functionality.
   */
  function init(form) {
    const steps = Array.from(form.querySelectorAll('[data-rx-step]'));
    // Steps 2 through last are skippable.
    const skippableSteps = steps.slice(1);

    steps.forEach((step) => {
      handleConditionals(step);
      // We attach the event listener to the step because an
      // `input[type="radio"]` element’s `change` event only fires when
      // selected, and not when it is unselected as a result of a different
      // radio button being selected. Evaluating all of the potentially
      // toggleable fields in the current step guarantees that we update
      // dependents for _all_ toggles in this step, not just the one that was
      // selected.
      step.addEventListener('change', (event) => {
        handleConditionals(event.currentTarget);
      });
    });
    window.componentEvents.on(`rx-form-reset-${form.id}`, () => {
      steps.forEach(handleConditionals);
    });
    window.componentEvents.on(`rx-form-partial-reset-${form.id}`, () => {
      steps.forEach(handleConditionals);
    });

    /**
     * Handle all operations for conditional fields within a given step.
     *
     * @param step A reference to the DOM Node for a given step.
     */
    function handleConditionals(step) {
      evaluateConditionalFields(step);
      evaluateSkippingFields(step);
      broadcastMessage(step);
    }

    /**
     * Evaluate any fields that should act as toggles within a given step and
     * process each one.
     */
    function evaluateConditionalFields(step) {
      const toggles = step.querySelectorAll('[data-rx-toggles]');
      toggles.forEach(updateDependents);
    }

    /**
     * Update the dependents of a given toggle across the entire form.
     */
    function updateDependents(toggle) {
      const selected = toggle.checked;
      const identifier = toggle.dataset.rxToggles;
      // There may be cross-step dependencies, so we search for dependents across
      // the entire form.
      const dependents = form
        .querySelectorAll(`[data-rx-toggled-via="${identifier}"]`);
      dependents.forEach((dependent) => {
        setDependentAttributes(dependent, selected);
      });
    }

    /**
     * Evaluate any fields that should skip configuration within a given step and
     * process each one.
     */
    function evaluateSkippingFields(step) {
      // If this isn’t the first step, do nothing.
      if (step.dataset.rxStep !== '1') return;
      const skippers = Array.from(step.querySelectorAll('[data-rx-skip-configuration="true"]'));
      const skipConfig = skippers.some((skipper) => skipper.checked);
      updateSkipConfigStatus(skipConfig);
    }

    /**
     * Enable/disable steps according to skipper field selection.
     */
    function updateSkipConfigStatus(skipConfig) {
      form.dataset.rxSkipConfigurationEnabled = skipConfig;
      skippableSteps.forEach((step) => {
        step.dataset.rxSkipStep = skipConfig;
        skipConfig ? removeRequiredAttrs(step) : restoreRequiredAttrs(step);
      });
      // Tell the consumer rx navigation module to update the visibility of the
      // submit button.
      window.componentEvents.emitEvent(`rx-form-update-submit-button-${form.id}`);
    }

    /**
     * Remove the required attribute from all fields within a given step and
     * track the requirement in a data-rx-skipped-step-required attribute.
     */
    function removeRequiredAttrs(step) {
      const fields = step.querySelectorAll('[required]');
      fields.forEach((field) => {
        field.required = false;
        field.dataset.rxSkippedStepRequired = true;
      });
    }

    /**
     * Restore the required attribute from all fields within a given step using
     * the data-rx-skipped-step-required attribute and remove the attribute.
     */
    function restoreRequiredAttrs(step) {
      const fields = step.querySelectorAll('[data-rx-skipped-step-required]');
      fields.forEach((field) => {
        field.required = true;
        delete field.dataset.rxSkippedStepRequired;
      });
    }

    /**
     * Set the appropriate attributes on a dependent according to their data
     * attribute settings and the state of their toggle.
     */
    function setDependentAttributes(dependent, selected) {
      const visibilitySetting = dependent.dataset.rxToggledVisibility;
      const requireSetting = dependent.dataset.rxToggledRequire;
      const enableSetting = dependent.dataset.rxToggledEnable;
      if (visibilitySetting) {
        // Set aria-hidden if:
        //   - toggle is selected & dependent is set to hidden
        //   - toggle is unselected & dependent is set to visible
        dependent.setAttribute('aria-hidden', selected ?
          visibilitySetting === 'hidden' : visibilitySetting === 'visible');
      }
      if (requireSetting) {
        // Set required if:
        //   - toggle is selected & dependent is set to be required
        //   - toggle is unselected & dependent is set to not be required
        dependent.required = (selected && requireSetting === 'true') ||
          (!selected && requireSetting === 'false');
      }
      if (enableSetting) {
        // Set disabled if:
        //   - toggle is selected & dependent is set not to be enabled
        //   - toggle is unselected & dependent is set to be enabled
        dependent.disabled = (selected && enableSetting === 'false') ||
          (!selected && enableSetting === 'true');
      }
    }

    /**
     * Emit an event indicating that form control attributes have been updated.
     */
    function broadcastMessage(step) {
      if (window.componentEvents) {
        const updatedStepNumber = step.dataset.rxStep;
        window.componentEvents
          .emitEvent(`rx-form-conditional-fields-updated-${form.id}`, [{ updatedStepNumber }]);
      }
    }
  }
})(window);
